import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  CoverLetterDocument,
  deleteCoverLetterDocument,
  listCoverLetterDocuments,
} from "features/cover-letter/api/CoverLetterApi";

interface CoverLetterContextValue {
  docs: CoverLetterDocument[];
  docsLoading: boolean;
  docsError: string;
  activeDoc: CoverLetterDocument | null;
  view: "list" | "editor";
  showNewModal: boolean;
  deleting: number | null;
  pendingDelete: CoverLetterDocument | null;
  loadDocs: () => Promise<void>;
  openDoc: (doc: CoverLetterDocument) => void;
  backToList: () => void;
  handleCreate: (doc: CoverLetterDocument) => void;
  requestDelete: (doc: CoverLetterDocument, e: React.MouseEvent) => void;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
  setShowNewModal: (show: boolean) => void;
}

const CoverLetterContext = createContext<CoverLetterContextValue | null>(null);

export function CoverLetterProvider({ children }: { children: React.ReactNode }) {
  const [docs, setDocs] = useState<CoverLetterDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState("");
  const [activeDoc, setActiveDoc] = useState<CoverLetterDocument | null>(null);
  const [view, setView] = useState<"list" | "editor">("list");
  const [showNewModal, setShowNewModal] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CoverLetterDocument | null>(null);

  const loadDocs = useCallback(async () => {
    setDocsError("");
    try {
      const data = await listCoverLetterDocuments();
      setDocs(data);
    } catch (e: any) {
      setDocsError(e.response?.data?.detail || "Failed to load documents");
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const openDoc = (doc: CoverLetterDocument) => {
    setActiveDoc(doc);
    setView("editor");
  };

  const backToList = () => {
    setView("list");
    setActiveDoc(null);
    // Refresh — a create or a category edit in the editor only updated local
    // state, not this list, so "already used" category checks elsewhere
    // (New Cover Letter modal, other editors) would otherwise see a stale snapshot.
    loadDocs();
  };

  const handleCreate = (doc: CoverLetterDocument) => {
    setShowNewModal(false);
    openDoc(doc);
  };

  const requestDelete = (doc: CoverLetterDocument, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDelete(doc);
  };

  const cancelDelete = () => setPendingDelete(null);

  const confirmDelete = async () => {
    const doc = pendingDelete;
    if (!doc) return;
    setDeleting(doc.id);
    try {
      await deleteCoverLetterDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e: any) {
      setDocsError(e.response?.data?.detail || "Could not delete document");
    } finally {
      setDeleting(null);
      setPendingDelete(null);
    }
  };

  return (
    <CoverLetterContext.Provider
      value={{
        docs,
        docsLoading,
        docsError,
        activeDoc,
        view,
        showNewModal,
        deleting,
        pendingDelete,
        loadDocs,
        openDoc,
        backToList,
        handleCreate,
        requestDelete,
        confirmDelete,
        cancelDelete,
        setShowNewModal,
      }}
    >
      {children}
    </CoverLetterContext.Provider>
  );
}

export function useCoverLetter(): CoverLetterContextValue {
  const ctx = useContext(CoverLetterContext);
  if (!ctx) throw new Error("useCoverLetter must be used inside CoverLetterProvider");
  return ctx;
}
